/**
 * StatsExplorer — the stats page. 2026-10-21 (per Gilles).
 *
 * ONE component for both audiences: the coach's Stats tab in Press Box and
 * the public page the families open from the shared link. Same layout, same
 * tables, same drill-down — only the data source and the depth differ (the
 * public side gets privacy-limited labels, published games only, and no
 * narrator quotes).
 *
 * Three layers deep, NHL style:
 *   1. Players / Goalies / Games tables (sortable, fuzzy-searchable)
 *   2. tap a player → their season, game by game
 *   3. tap a game   → the individual heard events (private: with the words)
 *
 * Export: CSV / Excel / PDF of the table on screen (utils/tableExport).
 */
import { useMemo, useState } from 'react';
import { Search, Download, ChevronRight, ArrowLeft, X, Loader2, Clock } from 'lucide-react';
import { fuzzyFilter } from '../../utils/fuzzy.js';
import { exportTable } from '../../utils/tableExport.js';
import { fmtGameTime } from '../../utils/pressBoxFormat.js';
import ClipButton from './ClipButton.jsx';
import Tip from '../Tip.jsx';
import { ReassignPicker } from './GoalTimeline.jsx';

const fmtDate = (d) => {
  if (!d) return '';
  const [y, m, day] = String(d).split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
};
const mmss = (sec) => { if (sec == null) return ''; const s0 = Math.round(sec); return `${Math.floor(s0 / 60)}:${String(s0 % 60).padStart(2, '0')}`; };
const foPct = (r) => { const t2 = (r.fow || 0) + (r.fol || 0); return t2 > 0 ? Math.round(((r.fow || 0) / t2) * 100) : null; };

const posTip = { F: 'Forward', D: 'Defense', G: 'Goalie' };
const labelWithPos = (r) => (<>{r.label}{r.pos ? <span style={{ color: 'var(--text-3)', fontWeight: 500, marginLeft: 6, fontSize: '0.85em' }} title={posTip[r.pos] || r.pos}>{r.pos}</span> : null}</>);
const SKATER_COLS = [
  { key: 'label', label: 'Player', left: true, render: labelWithPos },
  { key: 'gp', label: 'GP', title: 'Games played' },
  { key: 'g', label: 'G', title: 'Goals' },
  { key: 'a', label: 'A', title: 'Assists' },
  { key: 'p', label: 'P', title: 'Points' },
  { key: 'sog', label: 'SOG', title: 'Shots on goal' },
  { key: 'ms', label: 'MS', title: 'Missed shots' },
  { key: 'bs', label: 'BS', title: 'Blocked shots' },
  { key: 'fo', label: 'FO', title: 'Faceoffs won–lost', sortKey: 'fow', render: (r) => ((r.fow || 0) + (r.fol || 0) > 0 ? `${r.fow || 0}–${r.fol || 0}` : '') },
  { key: 'foPct', label: 'FO%', title: 'Faceoff win %', sortKey: '_foPct', render: (r) => (foPct(r) != null ? `${foPct(r)}%` : '') },
  { key: 'pim', label: 'PIM', title: 'Penalty minutes' },
];
const GOALIE_COLS = [
  { key: 'label', label: 'Goalie', left: true, render: labelWithPos },
  { key: 'gp', label: 'GP' },
  { key: 'sv', label: 'SV', title: 'Saves' },
  { key: 'ga', label: 'GA', title: 'Goals against' },
  { key: 'svPct', label: 'SV%', render: (r) => (r.svPct == null ? '' : r.svPct.toFixed(3).replace(/^0/, '')) },
];
// 2026-11-03 (per Gilles) — the default "All" table: every player, goalies
// included, with their SV / GA beside the skater columns
const ALL_COLS = [
  ...SKATER_COLS,
  { key: 'sv', label: 'SV', title: 'Saves (goalies)' },
  { key: 'ga', label: 'GA', title: 'Goals against (goalies)' },
];
const GAME_STAT_COLS = SKATER_COLS.filter(c => !['label', 'gp'].includes(c.key));

function SortableTable({ cols, rows, onRow, highlightTop = null, empty }) {
  const [sort, setSort] = useState({ key: null, dir: -1 });
  const sorted = useMemo(() => {
    if (!sort.key) return rows;
    const val = (r) => sort.key === '_foPct' ? (foPct(r) ?? -1) : (r[sort.key] ?? (typeof r[sort.key] === 'string' ? '' : -1));
    return rows.slice().sort((a, b) => {
      const av = val(a); const bv = val(b);
      if (typeof av === 'string' || typeof bv === 'string') return sort.dir * String(av).localeCompare(String(bv));
      return sort.dir * (av - bv);
    });
  }, [rows, sort]);
  const th = (c) => {
    const k = c.sortKey || c.key;
    const active = sort.key === k;
    return (
      <th key={c.key} title={c.title || ''} aria-sort={active ? (sort.dir < 0 ? 'descending' : 'ascending') : 'none'}
        onClick={() => setSort(s => ({ key: k, dir: s.key === k ? -s.dir : (c.left ? 1 : -1) }))}
        style={{ padding: '10px 10px', textAlign: c.left ? 'left' : 'right', fontSize: 11, cursor: 'pointer', userSelect: 'none',
          color: active ? 'var(--ice)' : 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap',
          borderBottom: '2px solid var(--border)', position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 1 }}>
        {c.label}{active ? (sort.dir < 0 ? ' ↓' : ' ↑') : ''}
      </th>
    );
  };
  return (
    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead><tr>{cols.map(th)}{onRow && <th style={{ width: 26, borderBottom: '2px solid var(--border)', position: 'sticky', top: 0, background: 'var(--surface)' }} />}</tr></thead>
        <tbody>
          {sorted.length === 0 && <tr><td colSpan={cols.length} style={{ padding: 20, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>{empty}</td></tr>}
          {sorted.map((r, i) => (
            <tr key={r.key || r.label + i} onClick={onRow ? () => onRow(r) : undefined} data-stats-row
              style={{ cursor: onRow ? 'pointer' : 'default', background: i % 2 ? 'var(--surface-2, transparent)' : 'transparent' }}
              onMouseEnter={(e) => { if (onRow) e.currentTarget.style.background = 'var(--ice-a8)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = i % 2 ? 'var(--surface-2, transparent)' : 'transparent'; }}>
              {cols.map((c) => (
                <td key={c.key} style={{ padding: '10px 10px', textAlign: c.left ? 'left' : 'right', whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums', borderBottom: '1px solid var(--border)',
                  fontWeight: c.key === 'p' || (c.left && highlightTop === i) ? 700 : (c.left ? 600 : 400),
                  color: c.key === 'p' ? 'var(--ice)' : 'var(--text)' }}>
                  {c.render ? c.render(r) : (r[c.key] || (r[c.key] === 0 ? (c.left ? '0' : '') : (c.left ? r[c.key] : '')))}
                </td>
              ))}
              {onRow && <td style={{ borderBottom: '1px solid var(--border)', padding: '10px 6px 10px 0' }}><ChevronRight size={13} style={{ color: 'var(--text-3)', display: 'block' }} /></td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Layer 2 + 3: one player, game by game, down to the heard events. */
function PlayerDrilldown({ row, breakdown, loading, onClose, gameHref, timeFormat, canSeeQuotes, teamId = null, roster = [], onChanged = null }) {
  const [openGame, setOpenGame] = useState(null);
  const [fixing, setFixing] = useState(null);     // event id being reassigned
  // 2026-10-22 (per Gilles) - tap a total (G, A, P, SOG...) to see exactly
  // which plays, in which games, add up to that number - words + audio.
  const [statFilter, setStatFilter] = useState(null);
  const FILTER_TYPES = { G: ['goal'], A: ['assist'], P: ['goal', 'assist'], SOG: ['shot'],
    'FO%': ['faceoff_win', 'faceoff_loss'], PIM: ['penalty_minutes'], SV: ['save'], GA: ['goal_against'], 'SV%': ['save', 'goal_against'], GP: null, SHARED: null };
  const matches = (e) => !statFilter || !FILTER_TYPES[statFilter] || FILTER_TYPES[statFilter].includes(e.type);
  const b = breakdown;
  const chip = (label, v) => (
    <button key={label} type="button" onClick={() => setStatFilter(f => (f === label || !FILTER_TYPES[label] ? null : label))}
      title={label === 'GP' ? 'Games played (tap any other total to see the plays behind it)' : label === 'SHARED' ? 'Games where both goalies played (a goalie change on the tape)' : `Tap to see every play behind this ${label} total — with the words heard and the audio`}
      style={{ textAlign: 'center', minWidth: 52, background: statFilter === label ? 'var(--ice-a8)' : 'none',
        border: statFilter === label ? '1px solid var(--ice)' : '1px solid transparent', borderRadius: 10, cursor: 'pointer', padding: '2px 4px' }}>
      <div className="display-num" style={{ fontSize: 24 }}>{v}</div>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', color: statFilter === label ? 'var(--ice)' : 'var(--text-3)' }}>{label}</div>
    </button>
  );
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} data-player-drilldown>
      <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '18px 18px 0 0', width: 'min(820px, 100vw)',
        maxHeight: '92vh', overflowY: 'auto', padding: '18px 16px 32px' }}>
        <div className="flex items-center gap-3 mb-3">
          <button type="button" className="btn-ghost" style={{ padding: '6px 10px' }} onClick={onClose}><ArrowLeft size={14} /> All players</button>
          <div className="flex items-center gap-3 flex-1" style={{ minWidth: 0 }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--ice-a8)', border: '1px solid var(--ice)', display: 'flex',
              alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: 'var(--ice)', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
              {row.number != null ? `#${row.number}` : '—'}
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="font-bold" style={{ fontSize: 18, overflowWrap: 'anywhere' }}>{b?.player?.name || row.label}</div>
              <div className="text-xs" style={{ color: 'var(--text-3)' }}>Season, game by game — tap a game for every recorded play</div>
            </div>
          </div>
          <button type="button" className="btn-ghost" style={{ padding: '6px 8px' }} onClick={onClose} aria-label="Close"><X size={15} /></button>
        </div>
        {loading && <div className="flex items-center gap-2 text-sm p-4" style={{ color: 'var(--text-2)' }}><Loader2 size={14} className="animate-spin" /> Loading…</div>}
        {b && (
          <>
            <div className="card mb-3 flex items-center gap-4" style={{ flexWrap: 'wrap', justifyContent: 'space-around' }}>
              {/* 2026-11-02 (per Gilles) — a goalie's line: games in net (and
                  how many shared with the other goalie), saves, goals against,
                  save % — never SOG (a shot pinned on him was a shot ON him) */}
              {b.player?.goalie ? (<>
                {chip('GP', b.totals.gp)}
                {b.totals.shared ? chip('SHARED', b.totals.shared) : null}
                {chip('SV', b.totals.sv)}{chip('GA', b.totals.ga)}
                {chip('SV%', (b.totals.sv + b.totals.ga) > 0 ? `${Math.round((b.totals.sv / (b.totals.sv + b.totals.ga)) * 1000) / 10}%` : '–')}
                {b.totals.g ? chip('G', b.totals.g) : null}{b.totals.a ? chip('A', b.totals.a) : null}
                {b.totals.pim ? chip('PIM', b.totals.pim) : null}
              </>) : (<>
              {chip('GP', b.totals.gp)}{chip('G', b.totals.g)}{chip('A', b.totals.a)}{chip('P', b.totals.p)}
              {chip('SOG', b.totals.sog)}{(b.totals.fow || b.totals.fol) ? chip('FO%', foPct(b.totals) != null ? `${foPct(b.totals)}%` : '–') : null}
              {b.totals.pim ? chip('PIM', b.totals.pim) : null}
              {(b.totals.sv || b.totals.ga) ? chip('SV', b.totals.sv) : null}
              </>)}
            </div>
            {statFilter && (
              <div className="text-xs mb-2 flex items-center gap-2" style={{ color: 'var(--ice)' }}>
                Showing only the plays behind <strong>{statFilter}</strong> — every game below is expanded.
                <button type="button" className="btn-ghost" style={{ padding: '2px 8px' }} onClick={() => setStatFilter(null)}>Show everything</button>
              </div>
            )}
            {b.games.length === 0 && <div className="card text-sm p-4 text-center" style={{ color: 'var(--text-3)' }}>No recorded plays yet.</div>}
            {b.games.filter(g => !statFilter || g.events.some(matches)).map(g => (
              <div key={g.gameId} className="card mb-2" style={{ padding: 0, overflow: 'hidden' }}>
                <button type="button" onClick={() => setOpenGame(openGame === g.gameId ? null : g.gameId)}
                  className="w-full flex items-center gap-3 text-left" style={{ background: 'transparent', border: 'none', padding: '10px 14px', cursor: 'pointer', color: 'var(--text)' }}>
                  <span className="text-sm" style={{ minWidth: 92, color: 'var(--text-2)' }}>{fmtDate(g.date)}</span>
                  <span className="flex-1 text-sm font-semibold" style={{ minWidth: 120 }}>vs {g.opponent || 'TBA'}</span>
                  {g.score && <span className="display-num" style={{ fontSize: 16 }}>{g.score.us}–{g.score.them}</span>}
                  {g.result && <span className="text-xs font-bold" style={{ color: g.result === 'W' ? 'var(--goal)' : g.result === 'L' ? 'var(--penalty)' : 'var(--text-2)', width: 14 }}>{g.result}</span>}
                  <span className="text-xs flex items-center gap-1.5" style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {/* goals and assists stand out from the rest (2026-10-21) */}
                    {g.stats.g > 0 && <span className="badge" style={{ background: 'var(--goal-a10)', color: 'var(--goal)', border: '1px solid var(--goal-a20)', fontWeight: 800 }}>{g.stats.g} G</span>}
                    {g.stats.a > 0 && <span className="badge" style={{ background: 'var(--ice-a8)', color: 'var(--ice)', border: '1px solid var(--ice-a20, var(--border))', fontWeight: 800 }}>{g.stats.a} A</span>}
                    {/* 2026-11-02 (per Gilles) — a goalie's game: in net alone, or shared */}
                    {g.net && (
                      <span className="badge" title={g.net.shared ? `Shared with ${g.net.with.map(w => w.label).join(', ')}${g.net.fromS ? ` — in net from ${mmss(g.net.fromS)} on the tape` : ' — started in net'}` : 'In net the whole game'}
                        style={{ background: g.net.shared ? 'var(--puck-a10)' : 'var(--ice-a8)', color: g.net.shared ? 'var(--puck)' : 'var(--ice)', border: '1px solid var(--border)', fontWeight: 700 }}>
                        {g.net.shared ? `shared with ${g.net.with.map(w => w.number != null ? `#${w.number}` : w.label).join(', ')}${g.net.fromS ? ` · from ${mmss(g.net.fromS)}` : ''}` : 'in net'}
                      </span>
                    )}
                    <span style={{ color: 'var(--text-2)' }}>
                      {g.net
                        ? [g.stats.sv ? `${g.stats.sv} SV` : null, g.stats.ga ? `${g.stats.ga} GA` : null].filter(Boolean).join(' · ') || 'no shot faced'
                        : ['sog', 'pim'].filter(k => g.stats[k]).map(k => `${g.stats[k]} ${k.toUpperCase()}`).join(' · ') || (!g.stats.g && !g.stats.a ? '—' : '')}
                    </span>
                  </span>
                  <ChevronRight size={14} style={{ color: 'var(--text-3)', transform: openGame === g.gameId ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
                </button>
                {(openGame === g.gameId || statFilter) && (
                  <div style={{ borderTop: '1px solid var(--border)', padding: '8px 14px 12px' }}>
                    {g.events.filter(matches).map((e, i) => (
                      <div key={i} className="py-1.5 text-sm" style={{ borderBottom: i < g.events.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <div className="flex items-baseline gap-3">
                          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-3)', minWidth: 42 }}>{e.t != null ? mmss(e.t) : ''}</span>
                          {e.period && <span style={{ fontSize: 11, color: 'var(--text-3)' }}>P{e.period}</span>}
                          <span style={{ fontWeight: e.type === 'goal' || e.type === 'assist' ? 800 : 600,
                            color: e.type === 'goal' ? 'var(--goal)' : e.type === 'assist' ? 'var(--ice)' : 'var(--text)' }}>
                            {e.label}{e.value > 1 ? ` ×${e.value}` : ''}
                          </span>
                          {canSeeQuotes && e.needsReview && <span className="badge" style={{ background: 'var(--puck-a10)', color: 'var(--puck)', fontSize: 10 }}>check</span>}
                          {canSeeQuotes && teamId && <ClipButton teamId={teamId} recordingId={e.recordingId} offsetMs={e.offsetMs} />}
                          {canSeeQuotes && teamId && e.id && e.gameId && (
                            <select value={e.type} title="Change what this call WAS — shot, save, goal… (sticks through AI re-runs)"
                              className="input-field" style={{ width: 'auto', padding: '1px 5px', fontSize: 11 }}
                              onClick={(ev0) => ev0.stopPropagation()}
                              onChange={async (ev3) => {
                                try {
                                  await api.patch(`/teams/${teamId}/game-events/${e.gameId}/${e.id}`, { event_type: ev3.target.value });
                                  onChanged?.();
                                } catch { /* toast lives in ReassignPicker paths */ }
                              }}>
                              {[['shot', 'Shot'], ['shot_missed', 'Missed shot'], ['shot_blocked', 'Blocked shot'], ['save', 'Save'],
                                ['goal', 'Goal'], ['assist', 'Assist'], ['faceoff_win', 'Faceoff won'], ['faceoff_loss', 'Faceoff lost'],
                                ['penalty_minutes', 'Penalty minutes']]
                                .map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                            </select>
                          )}
                          {canSeeQuotes && teamId && e.id && e.gameId && (
                            <Tip text="The AI got it wrong? Assign this play to another player — quick-search the roster and tap the right one. Your pick sticks through later AI re-runs.">
                              <button type="button" onClick={() => setFixing(fixing === e.id ? null : e.id)}
                                style={{ padding: '1px 9px', fontSize: 11, fontWeight: 700, borderRadius: 999, cursor: 'pointer',
                                  background: fixing === e.id ? 'var(--gold)' : 'var(--gold-a10, rgba(240,180,41,0.12))',
                                  color: fixing === e.id ? '#1a1a1a' : 'var(--gold)', border: '1px solid var(--gold)' }}>
                                {fixing === e.id ? 'Cancel' : 'Fix'}
                              </button>
                            </Tip>
                          )}
                          <span className="ml-auto text-xs" style={{ color: 'var(--ice)' }}>{/^voice/.test(e.source || '') ? '🎙 heard' : '✋ entered'}</span>
                        </div>
                        {canSeeQuotes && e.said && <div className="text-xs mt-0.5" style={{ color: 'var(--text-3)', fontStyle: 'italic', paddingLeft: 54 }}>“{e.said}”</div>}
                        {fixing === e.id && (
                          <ReassignPicker teamId={teamId} gameId={e.gameId} eventId={e.id} roster={roster}
                            current={b?.player?.label || null} onClose={() => setFixing(null)}
                            onDone={() => { setFixing(null); onChanged?.(); }} />
                        )}
                      </div>
                    ))}
                    {gameHref && <a className="text-xs" style={{ color: 'var(--ice)', textDecoration: 'none', display: 'inline-block', marginTop: 8 }} href={gameHref({ id: g.gameId })}>Full game page →</a>}
                  </div>
                )}
              </div>
            ))}
            {!canSeeQuotes && <div className="text-xs mt-2" style={{ color: 'var(--text-3)' }}>Times {timeFormat === '24h' ? 'use the 24-hour clock' : 'are tape times'}; what the narrator said stays with the team.</div>}
          </>
        )}
      </div>
    </div>
  );
}

export default function StatsExplorer({
  teamName, record, games = [], skaters = [], goalies = [], updatedAt = null, timeFormat = '12h',
  seasons = null, season = '', onSeason = null,
  phase = 'all', onPhase = null, phases = null,   // 2026-11-04: pre-season / regular / playoffs / tournaments
  fetchBreakdown,            // async (row) => breakdown payload
  gameHref,                  // (game) => url for a game row / drill-down link
  canSeeQuotes = false,      // private tab: show the narrator's words + review flags
  editTeamId = null,         // 2026-10-22: set on the coach's page - turns on listen + fix in the drill-down
  hiddenCols = [],           // 2026-10-23 (per Gilles): stat columns the narrator switched off
  exportName = 'stats',
}) {
  const hidden = new Set(hiddenCols || []);
  const SKATER_COLS_V = SKATER_COLS.filter(c => c.key === 'label' || !hidden.has(c.key));
  const GOALIE_COLS_V = GOALIE_COLS.filter(c => c.key === 'label' || !hidden.has(c.key));
  const ALL_COLS_V = ALL_COLS.filter(c => c.key === 'label' || !hidden.has(c.key));
  const [tab, setTab] = useState('all');
  const [q, setQ] = useState('');
  // 2026-10-21 (per Gilles) — a sort picker beside the tabs: jersey number is
  // the default; clicking a column header still overrides it.
  const [sortBy, setSortBy] = useState('number');
  const [sel, setSel] = useState(null);           // player row being drilled into
  const [breakdown, setBreakdown] = useState(null);
  const [bLoading, setBLoading] = useState(false);

  const played = useMemo(() => games.filter(g => g.score || g.official).slice().reverse(), [games]);
  const nameParts = (r) => {
    const txt = String(r.name || r.label || '').replace(/^#\d+\s*/, '').trim();
    const w = txt.split(/\s+/).filter(Boolean);
    return { first: w[0] || '', last: w.length > 1 ? w[w.length - 1] : (w[0] || '') };
  };
  const SORTS = {
    number: (a, b) => (a.number ?? 999) - (b.number ?? 999),
    first: (a, b) => nameParts(a).first.localeCompare(nameParts(b).first),
    last: (a, b) => nameParts(a).last.localeCompare(nameParts(b).last),
    g: (a, b) => (b.g || 0) - (a.g || 0) || (b.p || 0) - (a.p || 0),
    p: (a, b) => (b.p || 0) - (a.p || 0) || (b.g || 0) - (a.g || 0),
    sog: (a, b) => (b.sog || 0) - (a.sog || 0),
    foPct: (a, b) => (foPct(b) ?? -1) - (foPct(a) ?? -1),
  };
  const sortedSkaters = useMemo(() => {
    const fn = SORTS[sortBy] || SORTS.number;
    return skaters.slice().sort((a, b) => (a.label === 'Unknown') - (b.label === 'Unknown') || fn(a, b));
  }, [skaters, sortBy]);   // eslint-disable-line react-hooks/exhaustive-deps
  const fSkaters = useMemo(() => fuzzyFilter(q, sortedSkaters, (r) => [r.label, r.name, r.number != null ? `#${r.number} ${r.number}` : '']), [q, sortedSkaters]);
  const fGoalies = useMemo(() => fuzzyFilter(q, goalies, (r) => [r.label, r.name, r.number != null ? `#${r.number}` : '']), [q, goalies]);
  // "All": the skaters' rows, then the goalies' (a goalie who also has a
  // skater line - a phantom goal, say - is one row with both)
  const fAll = useMemo(() => {
    const byKey = new Map();
    const keyOf = (r) => r.userId || (r.number != null ? `num:${r.number}` : r.label);
    for (const r of sortedSkaters) byKey.set(keyOf(r), { ...r });
    for (const g of goalies) {
      const k = keyOf(g);
      const cur = byKey.get(k);
      if (cur) Object.assign(cur, { sv: g.sv, ga: g.ga, svPct: g.svPct, pos: cur.pos || 'G', gp: Math.max(cur.gp || 0, g.gp || 0) });
      else byKey.set(k, { ...g, g: 0, a: 0, p: 0, sog: 0, ms: 0, bs: 0, fow: 0, fol: 0, pim: 0, pos: g.pos || 'G' });
    }
    const rows = [...byKey.values()].sort((a, b) => (a.label === 'Unknown') - (b.label === 'Unknown') || (SORTS[sortBy] || SORTS.number)(a, b));
    return fuzzyFilter(q, rows, (r) => [r.label, r.name, r.number != null ? `#${r.number} ${r.number}` : '']);
  }, [q, sortedSkaters, goalies, sortBy]);   // eslint-disable-line react-hooks/exhaustive-deps
  const fGames = useMemo(() => fuzzyFilter(q, played, (g) => [g.opponent, fmtDate(g.date), g.date, g.result || '', g.score ? `${g.score.us}-${g.score.them}` : '']), [q, played]);

  const openPlayer = async (row) => {
    setSel(row); setBreakdown(null);
    if (!fetchBreakdown) return;
    setBLoading(true);
    try { setBreakdown(await fetchBreakdown(row)); } catch { /* panel shows loading-off state */ }
    finally { setBLoading(false); }
  };

  const doExport = (format) => {
    const stamp = { csv: 'csv', xlsx: 'xlsx', pdf: 'pdf' }[format];
    if (tab === 'games') {
      exportTable({ filename: `${exportName}_games`, title: `${teamName} — games`, format: stamp,
        columns: [{ key: 'date', label: 'Date' }, { key: 'opponent', label: 'Opponent' }, { key: 'score', label: 'Score' }, { key: 'result', label: 'Result' }],
        rows: fGames.map(g => ({ date: g.date, opponent: g.opponent || 'TBA', score: g.score ? `${g.score.us}-${g.score.them}` : '', result: g.result || '' })) });
    } else if (tab === 'goalies') {
      exportTable({ filename: `${exportName}_goalies`, title: `${teamName} — goalies`, format: stamp,
        columns: GOALIE_COLS_V.map(c => ({ key: c.key, label: c.label })),
        rows: fGoalies.map(r => ({ ...r, svPct: r.svPct != null ? r.svPct.toFixed(3) : '' })) });
    } else if (tab === 'all') {
      exportTable({ filename: `${exportName}_all`, title: `${teamName} — all players`, format: stamp,
        columns: [...ALL_COLS_V.filter(c => !['fo', 'foPct'].includes(c.key)).map(c => ({ key: c.key, label: c.label })),
                  { key: 'fow', label: 'FOW' }, { key: 'fol', label: 'FOL' }],
        rows: fAll });
    } else {
      exportTable({ filename: `${exportName}_players`, title: `${teamName} — player stats`, format: stamp,
        columns: [...SKATER_COLS_V.filter(c => !['fo', 'foPct'].includes(c.key)).map(c => ({ key: c.key, label: c.label })),
                  { key: 'fow', label: 'FOW' }, { key: 'fol', label: 'FOL' }],
        rows: fSkaters });
    }
  };

  const tabBtn = (id, label) => (
    <button key={id} type="button" onClick={() => setTab(id)} data-stats-tab={id}
      style={{ padding: '8px 14px', borderRadius: 999, fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
        border: `1px solid ${tab === id ? 'var(--ice)' : 'var(--border)'}`,
        background: tab === id ? 'var(--ice-a8)' : 'transparent', color: tab === id ? 'var(--ice)' : 'var(--text-2)' }}>
      {label}
    </button>
  );

  return (
    <div data-stats-explorer>
      {/* record strip + updated-as-of */}
      <div className="card mb-3">
        <div className="flex items-center gap-5" style={{ flexWrap: 'wrap' }}>
          {record && (
            <>
              <div><div className="label">Record</div><div className="display-num" style={{ fontSize: 30 }}>{record.w}–{record.l}–{record.t}</div></div>
              <div><div className="label">Goals for</div><div className="display-num" style={{ fontSize: 30, color: 'var(--goal)' }}>{record.gf}</div></div>
              <div><div className="label">Against</div><div className="display-num" style={{ fontSize: 30, color: 'var(--penalty)' }}>{record.ga}</div></div>
              {/* 2026-10-21 — team shooting + discipline at a glance */}
              <div><div className="label" title="Shots on net, for">SOG for</div><div className="display-num" style={{ fontSize: 30 }}>{skaters.reduce((n, r) => n + (r.sog || 0), 0)}</div></div>
              <div><div className="label" title="Shots on our net (saves + goals against)">SOG against</div><div className="display-num" style={{ fontSize: 30 }}>{goalies.reduce((n, r) => n + (r.sv || 0) + (r.ga || 0), 0)}</div></div>
              {(() => {
                const gp = played.length || 0;
                const pim = skaters.reduce((n, r) => n + (r.pim || 0), 0);
                return <div><div className="label" title="Penalty minutes per game">Avg PIM/G</div><div className="display-num" style={{ fontSize: 30 }}>{gp ? (pim / gp).toFixed(1) : '0.0'}</div></div>;
              })()}
            </>
          )}
          <div className="ml-auto" style={{ textAlign: 'right' }}>
            {seasons?.length > 0 && onSeason && (
              <select className="input-field mb-1" style={{ width: 'auto', padding: '7px 10px' }} value={season} onChange={(e) => onSeason(e.target.value)} aria-label="Season">
                {seasons.map(s => <option key={s.id} value={s.id}>{s.name}{s.active ? ' (current)' : ''}</option>)}
                <option value="all">All seasons</option>
              </select>
            )}
            {onPhase && (
              <select className="input-field mb-1" style={{ width: 'auto', padding: '7px 10px', marginLeft: 6 }} value={phase} onChange={(e) => onPhase(e.target.value)} aria-label="Part of the season" data-phase-picker
                title={phases ? 'Pre-season, regular season and playoffs follow the dates in Site & Settings; tournament games are the ones with a tournament name' : 'Set the pre-season / regular season / playoff dates in Site & Settings to split the stats by part of the season'}>
                <option value="all">Whole season</option>
                <option value="preseason">Pre-season</option>
                <option value="regular">Regular season</option>
                <option value="playoffs">Playoffs</option>
                <option value="tournament">Tournaments</option>
              </select>
            )}
            {updatedAt && (
              <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--text-3)', justifyContent: 'flex-end' }} data-updated-as-of>
                <Clock size={11} /> Updated {new Date(updatedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* controls: search · tabs · export */}
      <div className="flex items-center gap-2 mb-3" style={{ flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 180 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
          <input className="input-field" style={{ paddingLeft: 32, width: '100%' }} value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search players or games…" aria-label="Search" data-stats-search />
        </div>
        {['players', 'all'].includes(tab) && (
          <select className="input-field" style={{ width: 'auto', padding: '7px 10px', fontSize: 13 }} value={sortBy}
            onChange={(e) => setSortBy(e.target.value)} aria-label="Sort players" data-sort-picker>
            <option value="number">Sort: jersey #</option>
            <option value="first">Sort: first name</option>
            <option value="last">Sort: last name</option>
            <option value="g">Sort: goals</option>
            <option value="p">Sort: points</option>
            <option value="sog">Sort: shots</option>
            <option value="foPct">Sort: FO%</option>
          </select>
        )}
        <div className="flex items-center gap-1">
          {tabBtn('all', 'All')}
          {tabBtn('players', 'Players')}
          {goalies.length > 0 && tabBtn('goalies', 'Goalies')}
          {tabBtn('games', 'By game')}
        </div>
        <div className="flex items-center gap-1 ml-auto">
          {['csv', 'xlsx', 'pdf'].map(f => (
            <button key={f} type="button" className="btn-ghost" style={{ padding: '7px 10px', fontSize: 12 }} onClick={() => doExport(f)}
              title={`Download the ${tab} table as ${f.toUpperCase()}`}>
              <Download size={12} /> {f === 'xlsx' ? 'Excel' : f.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* the table */}
      <div className="card mb-4" style={{ padding: 0, overflow: 'hidden' }}>
        {tab === 'all' && (
          <SortableTable cols={ALL_COLS_V} rows={fAll} empty={q ? 'No player matches that search.' : 'No stats yet.'}
            onRow={fetchBreakdown ? openPlayer : null} />
        )}
        {tab === 'players' && (
          <SortableTable cols={SKATER_COLS_V} rows={fSkaters} empty={q ? 'No player matches that search.' : 'No stats yet.'}
            onRow={fetchBreakdown ? openPlayer : null} />
        )}
        {tab === 'goalies' && (
          <SortableTable cols={GOALIE_COLS_V} rows={fGoalies} empty="No goalie stats yet."
            onRow={fetchBreakdown ? openPlayer : null} />
        )}
        {tab === 'games' && (
          <div>
            {fGames.length === 0 && <div className="p-4 text-sm text-center" style={{ color: 'var(--text-3)' }}>{q ? 'No game matches that search.' : 'No games yet.'}</div>}
            {fGames.map(g => (
              <a key={g.id} href={gameHref ? gameHref(g) : '#'} className="flex items-center gap-3 px-3 py-2.5"
                style={{ borderBottom: '1px solid var(--border)', color: 'var(--text)', textDecoration: 'none' }} data-game-row>
                <span className="text-sm" style={{ minWidth: 96, color: 'var(--text-2)' }}>{fmtDate(g.date)}</span>
                <span className="text-xs" style={{ minWidth: 56, color: 'var(--text-3)' }}>{fmtGameTime(g.time, timeFormat)}</span>
                <span className="flex-1 text-sm font-semibold" style={{ minWidth: 120 }}>vs {g.opponent || 'TBA'}</span>
                <span className="display-num" style={{ fontSize: 18, minWidth: 54, textAlign: 'right' }}>{g.score ? `${g.score.us}–${g.score.them}` : '–'}</span>
                <span className="text-xs font-bold" style={{ minWidth: 14, color: g.result === 'W' ? 'var(--goal)' : g.result === 'L' ? 'var(--penalty)' : 'var(--text-2)' }}>{g.result || ''}</span>
                <ChevronRight size={14} style={{ color: 'var(--text-3)' }} />
              </a>
            ))}
          </div>
        )}
      </div>

      {sel && (
        <PlayerDrilldown row={sel} breakdown={breakdown} loading={bLoading} onClose={() => setSel(null)}
          gameHref={gameHref} timeFormat={timeFormat} canSeeQuotes={canSeeQuotes}
          teamId={editTeamId}
          roster={[...skaters, ...goalies].map(r => ({
            userId: /^[0-9a-f-]{36}$/i.test(String(r.userId || '')) ? r.userId : null,
            jerseyNumber: r.number, name: r.name || r.label,
          }))}
          onChanged={async () => { try { setBreakdown(await fetchBreakdown(sel)); } catch { /* keep what we have */ } }} />
      )}
    </div>
  );
}
